import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Form, Row, Col } from 'react-bootstrap';
import { attendanceAPI } from '../services/api';
import { formatDateIST, formatTimeIST } from '../utils/dateFormatter';
import { FaClock, FaSignInAlt, FaSignOutAlt, FaCalendarAlt, FaUpload, FaFileExcel } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import { useAuth } from '../contexts/AuthContext';

const Attendance = () => {
  const { isAdmin, isHR } = useAuth();
  const [attendances, setAttendances] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const totalWorkingHoursThreshold = Number(process.env.REACT_APP_TOTAL_WORKING_HOURS) || 8;

  useEffect(() => {
    fetchAttendances();
    fetchTodayAttendance();
    setCurrentPage(1);
  }, [month, year]);

  const fetchAttendances = async () => {
    try {
      const response = await attendanceAPI.getMyAttendance(month, year);
      setAttendances(response.data);
    } catch (error) {
      console.error('Error fetching attendances:', error);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const response = await attendanceAPI.getToday();
      setTodayAttendance(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handlePunchIn = async () => {
    try {
      await attendanceAPI.punchIn();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Punched in successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      fetchTodayAttendance();
      fetchAttendances();
      // Notify Navigation component to update timer
      window.dispatchEvent(new Event('attendanceUpdated'));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unknown error';
      const isIPRestriction = error.response?.status === 403;

      Swal.fire({
        icon: isIPRestriction ? 'warning' : 'error',
        title: isIPRestriction ? 'Access Restricted' : 'Error',
        text: errorMessage,
        confirmButtonColor: '#635bff',
        html: isIPRestriction
          ? `<div style="text-align: left; padding: 10px;">
              <p style="margin-bottom: 10px;">${errorMessage}</p>
              <p style="color: #6b7280; font-size: 14px;">Please check:</p>
              <ul style="color: #6b7280; font-size: 14px; text-align: left; margin-top: 8px;">
                <li>You are connected to the office network</li>
                <li>Your VPN is active (if applicable)</li>
                <li>Contact IT support if you need assistance</li>
              </ul>
            </div>`
          : undefined
      });
    }
  };

  const handlePunchOut = async () => {
    try {
      await attendanceAPI.punchOut(null);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Punched out successfully!',
        confirmButtonColor: '#635bff',
        timer: 2000,
        showConfirmButton: false
      });
      fetchTodayAttendance();
      fetchAttendances();
      // Notify Navigation component to update timer
      window.dispatchEvent(new Event('attendanceUpdated'));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Unknown error';
      const isIPRestriction = error.response?.status === 403;

      Swal.fire({
        icon: isIPRestriction ? 'warning' : 'error',
        title: isIPRestriction ? 'Access Restricted' : 'Error',
        text: errorMessage,
        confirmButtonColor: '#635bff',
        html: isIPRestriction
          ? `<div style="text-align: left; padding: 10px;">
              <p style="margin-bottom: 10px;">${errorMessage}</p>
              <p style="color: #6b7280; font-size: 14px;">Please check:</p>
              <ul style="color: #6b7280; font-size: 14px; text-align: left; margin-top: 8px;">
                <li>You are connected to the office network</li>
                <li>Your VPN is active (if applicable)</li>
                <li>Contact IT support if you need assistance</li>
              </ul>
            </div>`
          : undefined
      });
    }
  };

  const calculateWorkingHours = (punchIn, punchOut) => {
    if (!punchIn || !punchOut) return null;

    const startTime = new Date(punchIn);
    const endTime = new Date(punchOut);
    const diffMs = endTime - startTime;
    const diffHours = diffMs / (1000 * 60 * 60);

    return diffHours;
  };

  const formatWorkingHours = (hours) => {
    if (hours === null) return '-';

    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    if (minutes === 0) {
      return `${wholeHours}h`;
    }
    return `${wholeHours}h ${minutes}m`;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid File',
        text: 'Please upload an Excel file (.xlsx or .xls)',
        confirmButtonColor: '#635bff',
      });
      return;
    }

    setUploading(true);

    try {
      const response = await attendanceAPI.uploadBiometric(file);

      const { records_added, records_skipped, errors } = response.data;

      let html = `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 10px;">
            <strong>Records Added:</strong> ${records_added}<br/>
            <strong>Records Skipped:</strong> ${records_skipped}
          </p>
      `;

      if (errors && errors.length > 0) {
        html += `
          <p style="color: #dc2626; margin-top: 15px; margin-bottom: 5px;"><strong>Errors:</strong></p>
          <ul style="color: #6b7280; font-size: 13px; margin-top: 5px; max-height: 200px; overflow-y: auto;">
        `;
        errors.forEach(error => {
          html += `<li>${error}</li>`;
        });
        html += `</ul>`;
      }

      html += `</div>`;

      Swal.fire({
        icon: 'success',
        title: 'Upload Successful!',
        html: html,
        confirmButtonColor: '#635bff',
      });

      fetchAttendances();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to upload file';
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: errorMessage,
        confirmButtonColor: '#635bff',
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      {/* Header */}
      <div style={{
        marginBottom: '32px'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#0a0a0a',
          letterSpacing: '-0.02em',
          margin: 0
        }}>
          Attendance Tracking
        </h1>
      </div>

      {/* Biometric Upload Card - Only for HR/Admin */}
      {(isAdmin || isHR) && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
          marginBottom: '24px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#faf9ff'
          }}>
            <h5 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: '600',
              color: '#0a0a0a',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaFileExcel style={{ color: '#635bff' }} />
              Biometric Attendance Upload
            </h5>
          </div>
          <div style={{ padding: '24px' }}>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '16px'
            }}>
              Upload weekly attendance data from the biometric system. The system will automatically sync with existing manual punch-ins.
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current.click()}
                disabled={uploading}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  backgroundColor: uploading ? '#9ca3af' : '#635bff',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FaUpload />
                {uploading ? 'Uploading...' : 'Upload Excel File'}
              </button>
              <span style={{
                fontSize: '13px',
                color: '#9ca3af'
              }}>
                Supported formats: .xlsx, .xls
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Today's Status Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        marginBottom: '24px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#faf9ff'
        }}>
          <h5 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FaClock style={{ color: '#635bff' }} />
            Today's Status
          </h5>
        </div>
        <div style={{ padding: '24px' }}>
          {todayAttendance?.punch_in ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#f0f0ff',
                borderRadius: '8px',
                border: '1px solid #e5e0ff'
              }}>
                <FaSignInAlt size={20} style={{ color: '#635bff' }} />
                <div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Punch In</div>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#0a0a0a' }}>
                    {formatTimeIST(todayAttendance.punch_in)}
                  </div>
                </div>
              </div>
              {todayAttendance.punch_out ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  backgroundColor: '#f0f0ff',
                  borderRadius: '8px',
                  border: '1px solid #e5e0ff'
                }}>
                  <FaSignOutAlt size={20} style={{ color: '#635bff' }} />
                  <div>
                    <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '2px' }}>Punch Out</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#0a0a0a' }}>
                      {formatTimeIST(todayAttendance.punch_out)}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handlePunchOut}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    backgroundColor: '#ff8800',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center'
                  }}
                >
                  <FaSignOutAlt />
                  Punch Out
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={handlePunchIn}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                backgroundColor: '#635bff',
                color: 'white',
                border: 'none',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'center'
              }}
            >
              <FaSignInAlt />
              Punch In
            </button>
          )}
        </div>
      </div>

      {/* Attendance History Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#faf9ff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h5 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: '600',
            color: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <FaCalendarAlt style={{ color: '#635bff' }} />
            Attendance History
          </h5>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '15px',
                cursor: 'pointer'
              }}
            >
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={{
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                padding: '8px 12px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              {[2024, 2025, 2026].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table hover responsive style={{ margin: 0 }}>
            <thead style={{ backgroundColor: '#faf9ff' }}>
              <tr>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb'
                }}>Date</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb'
                }}>Punch In</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb'
                }}>Punch Out</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb'
                }}>Working Hours</th>
                <th style={{
                  padding: '16px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb'
                }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {attendances.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: '#9ca3af',
                    fontSize: '14px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                      No records found
                    </div>
                    <div>Your attendance records will appear here</div>
                  </td>
                </tr>
              ) : (
                (() => {
                  const indexOfLastItem = currentPage * itemsPerPage;
                  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                  const currentItems = attendances.slice(indexOfFirstItem, indexOfLastItem);

                  return currentItems.map((att) => (
                    <tr key={att.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#0a0a0a'
                      }}>
                        {formatDateIST(att.date)}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {att.punch_in ? formatTimeIST(att.punch_in) : '-'}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        color: '#374151'
                      }}>
                        {att.punch_out ? formatTimeIST(att.punch_out) : '-'}
                      </td>
                      <td style={{
                        padding: '16px 24px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        {(() => {
                          const workingHours = calculateWorkingHours(att.punch_in, att.punch_out);
                          const formattedHours = formatWorkingHours(workingHours);
                          const color = workingHours !== null
                            ? workingHours >= totalWorkingHoursThreshold
                              ? '#16a34a' // green
                              : '#dc2626' // red
                            : '#6b7280'; // gray for no data
                          return (
                            <span style={{ color }}>
                              {formattedHours}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{
                        padding: '16px 24px'
                      }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor:
                            att.status === 'present' ? '#e8e5ff' :
                            att.status === 'wfh' ? '#dcfce7' :
                            att.status === 'leave' ? '#fef3c7' :
                            att.status === 'half_day' ? '#dbeafe' :
                            '#fee2e2',
                          color:
                            att.status === 'present' ? '#635bff' :
                            att.status === 'wfh' ? '#166534' :
                            att.status === 'leave' ? '#92400e' :
                            att.status === 'half_day' ? '#1e40af' :
                            '#991b1b'
                        }}>
                          {att.status === 'wfh' ? 'WFH' : att.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ));
                })()
              )}
            </tbody>
          </Table>
        </div>
        {attendances.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(attendances.length / itemsPerPage)}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={attendances.length}
          />
        )}
      </div>
    </>
  );
};

export default Attendance;
